import React, { useEffect, useState } from 'react';
import { Clock, Plus, X, Loader2, AlertCircle, Calendar, ChevronDown, Trash2, Edit2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SchedulesProps {
  profile: any;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface WorkSchedule {
  id?: number;
  hora_entrada: string;
  hora_salida: string;
  dias_de_la_semana: string[];
}

interface SpecialDay {
  id?: number;
  fecha: string;
  razon: string;
  se_trabaja: boolean;
  hora_apertura: string | null;
  hora_cierre: string | null;
}

const daysOfWeek = [
  { value: 'Monday', label: 'Lunes' },
  { value: 'Tuesday', label: 'Martes' },
  { value: 'Wednesday', label: 'Miércoles' },
  { value: 'Thursday', label: 'Jueves' },
  { value: 'Friday', label: 'Viernes' },
  { value: 'Saturday', label: 'Sábado' },
  { value: 'Sunday', label: 'Domingo' }
];

export const Schedules: React.FC<SchedulesProps> = ({ profile, setError, setSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [workSchedules, setWorkSchedules] = useState<WorkSchedule[]>([]);
  const [specialDays, setSpecialDays] = useState<SpecialDay[]>([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showSpecialDayForm, setShowSpecialDayForm] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState<number | null>(null);
  const [editingSpecialDayId, setEditingSpecialDayId] = useState<number | null>(null);
  const [userTimezone, setUserTimezone] = useState<number>(-3); // Default timezone (UTC-3)
  const [newSchedule, setNewSchedule] = useState<WorkSchedule>({
    hora_entrada: '09:00',
    hora_salida: '18:00',
    dias_de_la_semana: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
  });
  const [newSpecialDay, setNewSpecialDay] = useState<SpecialDay>({
    fecha: new Date().toISOString().split('T')[0],
    razon: '',
    se_trabaja: false,
    hora_apertura: null,
    hora_cierre: null
  });

  useEffect(() => {
    if (profile) {
      fetchUserTimezone();
      fetchSchedules();
      fetchSpecialDays();
    }
  }, [profile]);

  const fetchUserTimezone = async () => {
    try {
      if (profile?.id_company) {
        const { data, error } = await supabase
          .from('config')
          .select('timezone')
          .eq('id_company', profile.id_company)
          .maybeSingle();
        
        if (error) throw error;
        if (data && data.timezone !== null) {
          setUserTimezone(data.timezone);
        }
      }
    } catch (err) {
      console.error('Error fetching user timezone:', err);
      // Fallback to default timezone if there's an error
    }
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      
      if (profile?.id_company) {
        const { data, error } = await supabase
          .from('horarios')
          .select('*')
          .eq('id_company', profile.id_company)
          .order('hora_entrada', { ascending: true });
        
        if (error) throw error;
        setWorkSchedules(data || []);
      }
    } catch (err) {
      console.error('Error fetching schedules:', err);
      setError(err instanceof Error ? err.message : 'Error loading schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialDays = async () => {
    try {
      if (profile?.id_company) {
        const { data, error } = await supabase
          .from('dias_no_laborables_especiales')
          .select('*')
          .eq('id_company', profile.id_company)
          .order('fecha', { ascending: true });
        
        if (error) throw error;
        setSpecialDays(data || []);
      }
    } catch (err) {
      console.error('Error fetching special days:', err);
    }
  };

  // Helper function to adjust time based on timezone
  const adjustTimeToTimezone = (time: string, timezone: number): string => {
    // Parse the time string (HH:MM) to get hours and minutes
    const [hours, minutes] = time.split(':').map(Number);
    
    // Create a Date object with today's date and the given time
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    
    // Apply the timezone offset (the timezone is stored as an integer representing hours)
    date.setHours(date.getHours() - timezone);
    
    // Format the result back to HH:MM format
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // Helper function to convert time from timezone to UTC
  const convertTimeFromTimezone = (time: string, timezone: number): string => {
    // Parse the time string (HH:MM) to get hours and minutes
    const [hours, minutes] = time.split(':').map(Number);
    
    // Create a Date object with today's date and the given time
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    
    // Apply the timezone offset in reverse (the timezone is stored as an integer representing hours)
    date.setHours(date.getHours() + timezone);
    
    // Format the result back to HH:MM format
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  const handleSaveSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (!profile?.id_company) throw new Error('No company associated with user');

      // Convert times from user's timezone to UTC for storage
      const scheduleToSave = {
        ...newSchedule,
        hora_entrada: convertTimeFromTimezone(newSchedule.hora_entrada, userTimezone),
        hora_salida: convertTimeFromTimezone(newSchedule.hora_salida, userTimezone)
      };

      if (editingScheduleId) {
        const { error } = await supabase
          .from('horarios')
          .update({
            hora_entrada: scheduleToSave.hora_entrada,
            hora_salida: scheduleToSave.hora_salida,
            dias_de_la_semana: scheduleToSave.dias_de_la_semana
          })
          .eq('id', editingScheduleId)
          .eq('id_company', profile.id_company);

        if (error) throw error;
        setSuccess('Horario actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('horarios')
          .insert({ ...scheduleToSave, id_company: profile.id_company });

        if (error) throw error;
        setSuccess('Horario agregado correctamente');
      }

      setNewSchedule({
        hora_entrada: '09:00',
        hora_salida: '18:00',
        dias_de_la_semana: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
      });
      setEditingScheduleId(null);
      setShowScheduleForm(false);
      await fetchSchedules();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar el horario');
    }
  };

  const handleDeleteSchedule = async (id: number) => {
    try {
      if (!profile?.id_company) return;

      const { error } = await supabase
        .from('horarios')
        .delete()
        .eq('id', id)
        .eq('id_company', profile.id_company);

      if (error) throw error;
      
      setWorkSchedules(prev => prev.filter(schedule => schedule.id !== id));
      setSuccess('Horario eliminado correctamente');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      setError('Error al eliminar el horario');
    }
  };

  const handleEditSchedule = (schedule: WorkSchedule) => {
    setEditingScheduleId(schedule.id ?? null);
    
    // Adjust times from UTC to user's timezone for display
    setNewSchedule({
      hora_entrada: adjustTimeToTimezone(schedule.hora_entrada, userTimezone),
      hora_salida: adjustTimeToTimezone(schedule.hora_salida, userTimezone),
      dias_de_la_semana: schedule.dias_de_la_semana
    });
    
    setShowScheduleForm(true);
  };

  const handleSaveSpecialDay = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (!profile?.id_company) throw new Error('No company associated with user');

      // Convert times from user's timezone to UTC for storage if they exist
      const specialDayToSave = {
        ...newSpecialDay,
        hora_apertura: newSpecialDay.hora_apertura 
          ? convertTimeFromTimezone(newSpecialDay.hora_apertura, userTimezone) 
          : null,
        hora_cierre: newSpecialDay.hora_cierre 
          ? convertTimeFromTimezone(newSpecialDay.hora_cierre, userTimezone) 
          : null
      };

      if (editingSpecialDayId) {
        const { error } = await supabase
          .from('dias_no_laborables_especiales')
          .update(specialDayToSave)
          .eq('id', editingSpecialDayId)
          .eq('id_company', profile.id_company);

        if (error) throw error;
        setSuccess('Día especial actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('dias_no_laborables_especiales')
          .insert({ ...specialDayToSave, id_company: profile.id_company });

        if (error) throw error;
        setSuccess('Día especial agregado correctamente');
      }

      setNewSpecialDay({
        fecha: new Date().toISOString().split('T')[0],
        razon: '',
        se_trabaja: false,
        hora_apertura: null,
        hora_cierre: null
      });
      setEditingSpecialDayId(null);
      setShowSpecialDayForm(false);
      await fetchSpecialDays();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving special day:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar el día especial');
    }
  };

  const handleDeleteSpecialDay = async (id: number) => {
    try {
      if (!profile?.id_company) return;

      const { error } = await supabase
        .from('dias_no_laborables_especiales')
        .delete()
        .eq('id', id)
        .eq('id_company', profile.id_company);

      if (error) throw error;
      
      setSpecialDays(prev => prev.filter(day => day.id !== id));
      setSuccess('Día especial eliminado correctamente');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting special day:', err);
      setError('Error al eliminar el día especial');
    }
  };

  const handleEditSpecialDay = (day: SpecialDay) => {
    setEditingSpecialDayId(day.id ?? null);
    
    // Adjust times from UTC to user's timezone for display if they exist
    setNewSpecialDay({
      fecha: day.fecha,
      razon: day.razon,
      se_trabaja: day.se_trabaja,
      hora_apertura: day.hora_apertura 
        ? adjustTimeToTimezone(day.hora_apertura, userTimezone) 
        : null,
      hora_cierre: day.hora_cierre 
        ? adjustTimeToTimezone(day.hora_cierre, userTimezone) 
        : null
    });
    
    setShowSpecialDayForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Work Schedule Section */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-gray-700" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Horario de Trabajo</h2>
              <p className="text-sm text-gray-500">Configura los horarios de atención de tu negocio</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {workSchedules.map((schedule) => (
                  <div 
                    key={schedule.id} 
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-200 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">
                          {daysOfWeek
                            .filter(day => schedule.dias_de_la_semana.includes(day.value))
                            .map(day => day.label)
                            .join(', ')}
                        </div>
                        <div className="font-medium text-gray-900">
                          {adjustTimeToTimezone(schedule.hora_entrada, userTimezone)} - {adjustTimeToTimezone(schedule.hora_salida, userTimezone)}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditSchedule(schedule)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteSchedule(schedule.id!)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {workSchedules.length === 0 && (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p>No hay horarios configurados</p>
                  </div>
                )}
              </div>

              {!showScheduleForm ? (
                <button
                  onClick={() => setShowScheduleForm(true)}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Agregar Horario
                </button>
              ) : (
                <div className="mt-6 border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {editingScheduleId ? 'Editar horario' : 'Agregar nuevo horario'}
                  </h3>
                  <form onSubmit={handleSaveSchedule} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hora de entrada
                        </label>
                        <input
                          type="time"
                          value={newSchedule.hora_entrada}
                          onChange={(e) => setNewSchedule({...newSchedule, hora_entrada: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hora de salida
                        </label>
                        <input
                          type="time"
                          value={newSchedule.hora_salida}
                          onChange={(e) => setNewSchedule({...newSchedule, hora_salida: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Días de la semana
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {daysOfWeek.map((day) => (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() => {
                              const isSelected = newSchedule.dias_de_la_semana.includes(day.value);
                              setNewSchedule({
                                ...newSchedule,
                                dias_de_la_semana: isSelected
                                  ? newSchedule.dias_de_la_semana.filter(d => d !== day.value)
                                  : [...newSchedule.dias_de_la_semana, day.value]
                              });
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              newSchedule.dias_de_la_semana.includes(day.value)
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowScheduleForm(false);
                          setEditingScheduleId(null);
                          setNewSchedule({
                            hora_entrada: '09:00',
                            hora_salida: '18:00',
                            dias_de_la_semana: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
                          });
                        }}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        {editingScheduleId ? 'Actualizar' : 'Guardar'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Special Days Section */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-gray-700" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Días Especiales</h2>
              <p className="text-sm text-gray-500">Configura los días no laborables y los horarios en días de jornada reducida</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            {specialDays.map((day) => (
              <div 
                key={day.id} 
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-200 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {new Date(day.fecha).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="text-sm text-gray-500">
                      {day.razon}
                      {day.se_trabaja && day.hora_apertura && day.hora_cierre && (
                        <span className="ml-2">
                          ({adjustTimeToTimezone(day.hora_apertura, userTimezone)} - {adjustTimeToTimezone(day.hora_cierre, userTimezone)})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditSpecialDay(day)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDeleteSpecialDay(day.id!)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {specialDays.length === 0 && (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p>No hay días especiales configurados</p>
              </div>
            )}
          </div>

          {!showSpecialDayForm ? (
            <button
              onClick={() => setShowSpecialDayForm(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Agregar Día Especial
            </button>
          ) : (
            <div className="mt-6 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingSpecialDayId ? 'Editar día especial' : 'Agregar nuevo día especial'}
              </h3>
              <form onSubmit={handleSaveSpecialDay} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha
                  </label>
                  <input
                    type="date"
                    value={newSpecialDay.fecha}
                    onChange={(e) => setNewSpecialDay({...newSpecialDay, fecha: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del día especial
                  </label>
                  <input
                    type="text"
                    value={newSpecialDay.razon}
                    onChange={(e) => setNewSpecialDay({...newSpecialDay, razon: e.target.value})}
                    placeholder="ej. Navidad"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="se_trabaja"
                    checked={newSpecialDay.se_trabaja}
                    onChange={(e) => setNewSpecialDay({
                      ...newSpecialDay,
                      se_trabaja: e.target.checked,
                      hora_apertura: e.target.checked ? '09:00' : null,
                      hora_cierre: e.target.checked ? '18:00' : null
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="se_trabaja" className="text-sm text-gray-700">
                    ¿Se trabaja este día?
                  </label>
                </div>

                {newSpecialDay.se_trabaja && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hora de apertura
                      </label>
                      <input
                        type="time"
                        value={newSpecialDay.hora_apertura || ''}
                        onChange={(e) => setNewSpecialDay({...newSpecialDay, hora_apertura: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hora de cierre
                      </label>
                      <input
                        type="time"
                        value={newSpecialDay.hora_cierre || ''}
                        onChange={(e) => setNewSpecialDay({...newSpecialDay, hora_cierre: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSpecialDayForm(false);
                      setEditingSpecialDayId(null);
                      setNewSpecialDay({
                        fecha: new Date().toISOString().split('T')[0],
                        razon: '',
                        se_trabaja: false,
                        hora_apertura: null,
                        hora_cierre: null
                      });
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    {editingSpecialDayId ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};